import {VueController} from '@/service/support/vuex-controller';
import orgService from "@/service/org-service";

export default VueController("WebhookSettings", {
	state: [
		"organization",
		"organizationId",
		"cognito",
		"user"
	],
	data() {
		return {
			loading: false,
			webhooks: [],
			webhookHeaders: [
				{
					text: "URL",
					align: 'start',
					sortable: true,
					value: 'url'
				},
				{
					text: "Events",
					value: 'events'
				},
				{
					text: "Status",
					value: 'enabled'
				},
				{
					text: "Success / Failures",
					value: 'stats'
				},
				{
					text: "Last Triggered",
					value: 'last_triggered'
				},
				{
					text: "Actions",
					value: 'actions',
					sortable: false
				}
			],
			deliveryHeaders: [
				{
					text: "Event",
					value: 'event_type'
				},
				{
					text: "Status",
					value: 'status'
				},
				{
					text: "HTTP Code",
					value: 'http_status_code'
				},
				{
					text: "Attempts",
					value: 'attempt_count'
				},
				{
					text: "Timestamp",
					value: 'created_at'
				}
			],
			showNewWebhookDialog: false,
			showDeliveriesDialog: false,
			selectedWebhook: null,
			deliveries: [],
			loadingDeliveries: false,
			newWebhook: {
				url: '',
				events: []
			},
			saving: false,
			deleting: {},
			testing: {},
			toggling: {},
			availableEvents: [
				{ value: 'portal.created', text: 'Portal Created', description: 'Triggered when a new portal is created' },
				{ value: 'portal.workflow_changed', text: 'Workflow Changed', description: 'Triggered when a portal workflow state changes' },
				{ value: 'file.uploaded', text: 'File Uploaded', description: 'Triggered when a file is uploaded to a portal' },
				{ value: 'file.signed', text: 'PDF Signed', description: 'Triggered when a PDF signature is completed' },
				{ value: 'task.created', text: 'Task Created', description: 'Triggered when a new task is created' },
				{ value: 'task.completed', text: 'Task Completed', description: 'Triggered when a task is marked as done' },
				{ value: 'guest.invited', text: 'Guest Invited', description: 'Triggered when a guest is invited to a portal' }
			],
			eventRules: {
				required: value => (value && value.length > 0) || 'At least one event is required'
			},
			urlRules: {
				required: value => (value && value.trim().length > 0) || 'Webhook URL is required',
				validUrl: value => {
					if (!value) return true;
					try {
						const url = new URL(value);
						return (url.protocol === 'http:' || url.protocol === 'https:') || 'Must be a valid HTTP or HTTPS URL';
					} catch (e) {
						return 'Must be a valid URL';
					}
				}
			}
		};
	},
	computed: {
		canManageWebhooks() {
			if (!this.organization) return false;
			// Only admins can manage webhook settings
			const userType = this.cognito && this.cognito.attributes ? this.cognito.attributes["custom:mutableUserType"] : null;
			return userType === 'admin';
		},
		webhooksEnabled() {
			return this.organization && this.organization.webhooksEnabled ? this.organization.webhooksEnabled : false;
		},
		isEnterpriseTier() {
			const tier = this.organization ? this.organization.tier : '';
			return tier === 'enterprise';
		},
		formattedWebhooks() {
			return this.webhooks.map(webhook => ({
				...webhook,
				events_display: webhook.events ? webhook.events.join(', ') : '',
				last_triggered_display: webhook.last_triggered ? new Date(webhook.last_triggered).toLocaleString() : 'Never',
				stats_display: (webhook.success_count || 0) + ' / ' + (webhook.failure_count || 0)
			}));
		},
		isNewWebhookFormValid() {
			if (!this.newWebhook.url || this.newWebhook.url.trim().length === 0) return false;
			if (!this.newWebhook.events || this.newWebhook.events.length === 0) return false;
			
			// Validate URL
			try {
				const url = new URL(this.newWebhook.url);
				if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
			} catch (e) {
				return false;
			}
			
			return true;
		}
	},
	mounted() {
		this.loadWebhooks();
	},
	methods: {
		loadWebhooks() {
			if (!this.canManageWebhooks || !this.isEnterpriseTier) return;
			
			this.loading = true;
			orgService.query("getWebhooks", undefined, undefined, {vuexCache: false})
				.then(response => {
					this.webhooks = response || [];
					this.loading = false;
				})
				.catch(error => {
					console.error("Error loading webhooks:", error);
					this.notification('error', 'Failed to load webhooks');
					this.loading = false;
				});
		},
		
		openNewWebhookDialog() {
			this.newWebhook = {
				url: '',
				events: []
			};
			this.showNewWebhookDialog = true;
		},
		
		async createWebhook() {
			if (!this.isNewWebhookFormValid) {
				this.notification('error', 'Please fill in all required fields correctly');
				return;
			}
			
			this.saving = true;
			
			try {
				await orgService.mutation("createWebhook", {
					url: this.newWebhook.url.trim(),
					events: this.newWebhook.events
				});
				
				this.showNewWebhookDialog = false;
				this.notification('success', 'Webhook created successfully');
				this.loadWebhooks();
			} catch (error) {
				console.error("Error creating webhook:", error);
				this.notification('error', 'Failed to create webhook: ' + (error.message || 'Unknown error'));
			} finally {
				this.saving = false;
			}
		},
		
		async deleteWebhook(webhook) {
			if (!confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
				return;
			}
			
			this.$set(this.deleting, webhook.webhook_id, true);
			
			try {
				await orgService.mutation("deleteWebhook", {
					webhook_id: webhook.webhook_id
				});
				
				this.notification('success', 'Webhook deleted successfully');
				this.loadWebhooks();
			} catch (error) {
				console.error("Error deleting webhook:", error);
				this.notification('error', 'Failed to delete webhook: ' + (error.message || 'Unknown error'));
			} finally {
				this.$set(this.deleting, webhook.webhook_id, false);
			}
		},
		
		async toggleWebhook(webhook) {
			this.$set(this.toggling, webhook.webhook_id, true);
			
			try {
				await orgService.mutation("updateWebhook", {
					webhook_id: webhook.webhook_id,
					enabled: !webhook.enabled
				});
				
				this.notification('success', 'Webhook ' + (webhook.enabled ? 'disabled' : 'enabled') + ' successfully');
				this.loadWebhooks();
			} catch (error) {
				console.error("Error toggling webhook:", error);
				this.notification('error', 'Failed to toggle webhook: ' + (error.message || 'Unknown error'));
			} finally {
				this.$set(this.toggling, webhook.webhook_id, false);
			}
		},
		
		async testWebhook(webhook) {
			this.$set(this.testing, webhook.webhook_id, true);
			
			try {
				await orgService.mutation("testWebhook", {
					webhook_id: webhook.webhook_id
				});
				
				this.notification('success', 'Test webhook sent successfully');
			} catch (error) {
				console.error("Error testing webhook:", error);
				this.notification('error', 'Failed to send test webhook: ' + (error.message || 'Unknown error'));
			} finally {
				this.$set(this.testing, webhook.webhook_id, false);
			}
		},
		
		copyToClipboard(text, label) {
			if (!text) return;
			
			navigator.clipboard.writeText(text).then(() => {
				this.notification('success', label + ' copied to clipboard');
			}).catch(err => {
				console.error('Failed to copy:', err);
				this.notification('error', 'Failed to copy to clipboard');
			});
		},
		
		async viewDeliveries(webhook) {
			this.selectedWebhook = webhook;
			this.showDeliveriesDialog = true;
			this.loadingDeliveries = true;
			
			try {
				const response = await orgService.query("getWebhookDeliveries", {
					webhook_id: webhook.webhook_id,
					limit: 50
				}, undefined, {vuexCache: false});
				
				this.deliveries = (response || []).map(delivery => ({
					...delivery,
					created_display: delivery.created_at ? new Date(delivery.created_at).toLocaleString() : '',
					status_color: delivery.status === 'success' ? 'success' : 'error'
				}));
			} catch (error) {
				console.error("Error loading webhook deliveries:", error);
				this.notification('error', 'Failed to load webhook deliveries');
			} finally {
				this.loadingDeliveries = false;
			}
		},
		
		closeDeliveriesDialog() {
			this.showDeliveriesDialog = false;
			this.selectedWebhook = null;
			this.deliveries = [];
		},
		
		getEventDescription(eventValue) {
			const event = this.availableEvents.find(e => e.value === eventValue);
			return event ? event.description : eventValue;
		}
	}
});

